import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { BRAND } from '../config/branding';
import './NotificationSettings.css';

function NotificationSettings() {
  const [preferences, setPreferences] = useState({
    email_enabled: true,
    login_alerts: true,
    task_assignments: true,
    task_reminders: true,
    certificate_expiry: true,
    mfa_alerts: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await axiosClient.get('/notifications/preferences');
      setPreferences(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load preferences:', err);
      setError('Failed to load notification preferences');
      setLoading(false);
    }
  };

  const handleToggle = async (field) => {
    const newValue = !preferences[field];
    const updatedPreferences = { ...preferences, [field]: newValue };
    
    // Optimistic update
    setPreferences(updatedPreferences);
    setSaving(true);
    setMessage('');
    setError('');

    try {
      await axiosClient.put('/notifications/preferences', { [field]: newValue });
      setMessage('Preferences updated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to update preferences:', err);
      setError('Failed to update preferences');
      // Revert on error
      setPreferences(preferences);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="notification-settings">
        <h2>Email Notifications</h2>
        <p>Loading preferences...</p>
      </div>
    );
  }

  return (
    <div className="notification-settings">
      <h2>Email Notifications</h2>
      <p className="settings-description">
        Manage your email notification preferences. You'll receive emails for the events you enable below.
      </p>

      {message && <div className="message success">{message}</div>}
      {error && <div className="message error">{error}</div>}

      <div className="preference-group">
        <div className="preference-item master">
          <div className="preference-info">
            <h3>Email Notifications</h3>
            <p>Master switch for all email notifications</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={preferences.email_enabled}
              onChange={() => handleToggle('email_enabled')}
              disabled={saving}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className={`preferences-list ${!preferences.email_enabled ? 'disabled' : ''}`}>
          <div className="preference-item">
            <div className="preference-info">
              <h4>Login Alerts</h4>
              <p>Get notified when you log in from a new device or location</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.login_alerts}
                onChange={() => handleToggle('login_alerts')}
                disabled={saving || !preferences.email_enabled}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="preference-item">
            <div className="preference-info">
              <h4>Task Assignments</h4>
              <p>Receive emails when tasks are assigned to you</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.task_assignments}
                onChange={() => handleToggle('task_assignments')}
                disabled={saving || !preferences.email_enabled}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="preference-item">
            <div className="preference-info">
              <h4> Task Reminders</h4>
              <p>Get reminders about upcoming task deadlines</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.task_reminders}
                onChange={() => handleToggle('task_reminders')}
                disabled={saving || !preferences.email_enabled}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="preference-item">
            <div className="preference-info">
              <h4>Certificate Expiry</h4>
              <p>Alerts when ISO certificates are approaching expiration</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.certificate_expiry}
                onChange={() => handleToggle('certificate_expiry')}
                disabled={saving || !preferences.email_enabled}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="preference-item">
            <div className="preference-info">
              <h4>MFA Alerts</h4>
              <p>Notifications about MFA setup and security changes</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.mfa_alerts}
                onChange={() => handleToggle('mfa_alerts')}
                disabled={saving || !preferences.email_enabled}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      <div className="info-box">
        <h4> About Email Notifications</h4>
        <ul>
          <li>Emails are sent from the {BRAND.name} notification system</li>
          <li>You can disable notifications at any time</li>
          <li>Some critical security alerts may still be sent</li>
          <li>Configure your SMTP settings in the backend .env file</li>
        </ul>
      </div>
    </div>
  );
}

export default NotificationSettings;
